import { Component, DoCheck, HostListener, OnDestroy } from "@angular/core";
import { UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { AlertController, LoadingController, ModalController, ToastController } from "@ionic/angular";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { BaseClass } from "src/app/shared/classes/base";
import { ProcessConfig, QuestionConfig, QuestionGroup } from "src/app/shared/interfaces/process-config.interface";
import { ProcessStatusEnum } from "src/app/shared/interfaces/process.interface";
import { UserPreview } from "src/app/shared/interfaces/user-preview.interface";
import { NewGoalsInput } from "src/app/shared/modals/new-goals-input/new-goals-input";
import { QuestionAttributePipe } from "src/app/shared/pipes/question-attribute.pipe";
import { QuestionGroupAttributesPipe } from "src/app/shared/pipes/question-group-attributes.pipe";
import { TranslationService } from "src/app/shared/services/translation.service";
import { GoalFeedback } from "../../_interfaces/goal-feedback.interface";
import { Goal, GoalStatusEnum } from "../../_interfaces/goal.interface";
import { TwoPlusTwoCoaching, TwoPlusTwoCoachingStatus } from "../../_interfaces/two-plus-two-coaching.interface";
import { GoalsService } from "../../goals.service";
import { ConfigService } from "src/app/shared/services/multi-clients/config.service";
import { AttributeType } from "src/app/shared/modals/filters/models/filter-models";

@Component({
    selector: "app-review-input-validation",
    templateUrl: "./review-input-validation.page.html",
    styleUrls: ["./review-input-validation.page.scss"],
})
export class ReviewInputValidationPage extends BaseClass implements OnDestroy, DoCheck {
    public hideGoalsOnGoalsReview: boolean = false;
    public showSkipTwoPlusTwoCoachingEvaluationFlag: boolean = false;
    public showSkipTwoPlusTwoCoachingEvaluation = false;

    public user: UserPreview;
    public inputConfig: ProcessConfig;
    public reviewConfig: ProcessConfig;
    public coachingConfig: ProcessConfig;
    public coachingForm: UntypedFormGroup;
    public goals: Array<Goal>;
    public displayedGoals: Array<Goal> = [];
    public userPreview: UserPreview;
    public subordinateGoals: any;
    public generalComments: string;
    public selectedLanguage: string;
    public canValidate: boolean = false;
    public isUserAttributesInsideConsentGoals: boolean = false;
    public refusalPendingQuestions: { [key: string]: boolean } = {};
    public coachingRecordStatus: TwoPlusTwoCoachingStatus;
    public TwoPlusTwoCoachingStatus = TwoPlusTwoCoachingStatus;

    private _participantId: string;
    private _searchSubject: Subject<string> = new Subject();
    private _returnPath: string = undefined;

    private readonly _cycleId: number;
    private readonly _employeeId: string;

    constructor(
        protected _toastController: ToastController,
        protected _translationService: TranslationService,
        private _modalController: ModalController,
        private _loadingController: LoadingController,
        private _alertController: AlertController,
        private _router: Router,
        private _goalsService: GoalsService,
        private _activatedRoute: ActivatedRoute,
        private _questionAttributePipe: QuestionAttributePipe,
        private _questionGroupAttributesPipe: QuestionGroupAttributesPipe,
        private _configService: ConfigService,
    ) {
        super(_toastController, _translationService);
        this._employeeId = this._activatedRoute.snapshot.paramMap.get("userId");
        this._cycleId = parseInt(
            this._activatedRoute.snapshot.paramMap.get("cycleId"),
            10
        );
        this.hideGoalsOnGoalsReview = this._configService.settings.features.hideGoalsOnGoalsReview;
        this.showSkipTwoPlusTwoCoachingEvaluationFlag = this._configService.settings.features.showSkipTwoPlusTwoCoachingEvaluation;
        this._setSearchSubscription();
    }

    ionViewWillEnter() {
        this.selectedLanguage = this._translationService.getSelectedLanguage();
        this._loadGoals(this._cycleId, this._employeeId);
        this._returnPath = localStorage.getItem("GoalsReview_ReturnPath");
    }

    public getColumnSize(questionsCount: number): number {
        let tableSize = 10;
        if (this.inputConfig) {
            tableSize = this.inputConfig.title ? tableSize - 2 : tableSize;
            tableSize = this.inputConfig.weight ? tableSize - 2 : tableSize;
            tableSize = this.inputConfig.deadline ? tableSize - 2 : tableSize;
            tableSize = this.inputConfig.description ? tableSize - 2 : tableSize;
        }

        let columnSize = questionsCount ? parseFloat((tableSize / questionsCount).toFixed(2).toString()) : null;

        if (columnSize) return columnSize;
        return questionsCount > 1 ? 1 : 2;
    }

    public updateSearch(searchTextValue: string) {
        this._searchSubject.next(searchTextValue);
    }

    private readonly ROMAN_NUMERALS = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
    private _sectionRegistry = new Map<string, number>();
    private _sectionCount = 0;

    ngDoCheck(): void {
        this._sectionRegistry.clear();
        this._sectionCount = 0;
    }

    public getSectionNumber(key: string): string {
        if (!this._sectionRegistry.has(key)) {
            this._sectionRegistry.set(key, ++this._sectionCount);
        }
        return this.ROMAN_NUMERALS[this._sectionRegistry.get(key)! - 1] ?? String(this._sectionRegistry.get(key));
    }

    public setApprovedAndClearComment(question: any) {
        if (this.canValidate && this.coachingRecordStatus !== TwoPlusTwoCoachingStatus.Authorized) {
            question.approved = true;
            this.clearQuestionRefuseMode(question);
            this.coachingForm.get('field' + question.order + 'Comment').setValue('');
        }
    }

    public setGoalApproved(goal: Goal): void {
        if (this.canValidate) {
            goal.approved = true;
            goal.managerComment = '';
        }
    }

    public setGoalRefused(goal: Goal): void {
        if (this.canValidate) {
            goal.approved = false;
        }
    }

    public startQuestionRefuseMode(question: any): void {
        if (!this.canValidate || this.coachingRecordStatus === TwoPlusTwoCoachingStatus.Authorized) return;
        const key = this.getQuestionKey(question);
        this.refusalPendingQuestions[key] = true;
        question.approved = false;
    }

    public confirmQuestionRefuse(question: any): void {
        if (!this.canValidate) return;
        question.approved = false;
        this.clearQuestionRefuseMode(question);
    }

    public cancelQuestionRefuse(question: any): void {
        question.approved = undefined;
        this.clearQuestionRefuseMode(question);
    }

    public isQuestionRefuseMode(question: any): boolean {
        return !!this.refusalPendingQuestions[this.getQuestionKey(question)];
    }

    public isPersistedQuestionRefused(question: any): boolean {
        return question?.approved === false && !!this.getQuestionCommentValue(question)?.trim();
    }

    public getQuestionCommentValue(question: any): string {
        const order = question?.order ?? question?.currentIndex;
        if (!order || !this.coachingForm) return '';

        const control = this.coachingForm.get('field' + order + 'Comment');
        return String(control?.value ?? '');
    }

    private clearQuestionRefuseMode(question: any): void {
        const key = this.getQuestionKey(question);
        if (this.refusalPendingQuestions[key]) {
            delete this.refusalPendingQuestions[key];
        }
    }

    private getQuestionKey(question: any): string {
        return String(question?.order ?? question?.currentIndex ?? '');
    }

    public setGoalsFeedbacks(goals: Array<Goal>): void {
        const feedbacks = this.reviewConfig.twoPlusTwoCoachingIntegration ? this._getGoalsFeedbacks(goals) : null;
        const isTwoPlusTwoCoachingIntegration = this.reviewConfig.twoPlusTwoCoachingIntegration;
        // Use only questions visible to this user (filtered by group and question attributes)
        let questions: QuestionConfig[] = this._getVisibleQuestions();
        let coachingFormVar = this.coachingForm.getRawValue();
        questions.forEach((question) => {
            if (question.approved) {
                let field = "field" + question.order + "Comment";
                if (!this.showSkipTwoPlusTwoCoachingEvaluation) {
                    coachingFormVar[field] = field.includes("field4") ? coachingFormVar[field] : "";
                }
            }
        });

        const coaching = this._getCoachingReview(questions, coachingFormVar);

        if (!coaching) {
            return;
        }

        const questionsToHideFlagged = this._questionAttributePipe.transform(questions, this.user.attributes);
        let allRequiredAnswered = true;
        const missingRequiredQuestions: QuestionConfig[] = [];
        const missingKeys = new Set<string>();
        for (let i = 0; i < questionsToHideFlagged.length; i++) {
            const question = questionsToHideFlagged[i];
            const fieldName = `field${question.order}`;

            const fieldValue = coaching[fieldName as keyof typeof coaching];

            if (question.required && !question.hideQuestion) {
                if (fieldValue == null || fieldValue === '') {
                    const key = `order-${question.order}`;
                    if (!missingKeys.has(key)){
                        missingKeys.add(key);
                        missingRequiredQuestions.push(question);
                    }
                }
            }
        }
        allRequiredAnswered = missingRequiredQuestions.length === 0;
        if (this.showSkipTwoPlusTwoCoachingEvaluationFlag) {
            let localAllRequiredAnswered = true;
            const missingRequiredComments: QuestionConfig[] = [];
            const missingCommentKeys = new Set<string>();
            for (let i = 0; i < questionsToHideFlagged.length; i++) {
                const question = questionsToHideFlagged[i];
                const fieldName = `field${question.currentIndex}Comment`;
                const fieldValue = coaching[fieldName as keyof typeof coaching];

                if (question.required && !question.hideQuestion) {
                    if (fieldValue == null || fieldValue === '') {
                    const key = `order-${question.order}`;
                    if (!missingCommentKeys.has(key)){
                        missingCommentKeys.add(key);
                        missingRequiredComments.push(question);
                    }
                    }
                }
            }

            localAllRequiredAnswered = missingRequiredComments.length === 0;

            if (!localAllRequiredAnswered) {
                this._armMandatoryQuestionFlag(missingRequiredComments, 'comment');
                this.notify(this._buildMissingFieldMessage(missingRequiredComments), "danger");
                return;
            }
        }

        if (coaching && !this.showSkipTwoPlusTwoCoachingEvaluationFlag && this.reviewConfig.twoPlusTwoCoachingIntegration && !allRequiredAnswered) {
            this._armMandatoryQuestionFlag(missingRequiredQuestions, 'answer');
            this.notify(this._buildMissingFieldMessage(missingRequiredQuestions), "danger");
        } else {
            if ((!this.reviewConfig.twoPlusTwoCoachingIntegration && coaching && !feedbacks) || (this.reviewConfig.twoPlusTwoCoachingIntegration && feedbacks && coaching)) {
                this._saveFeedbacks(coaching, feedbacks, isTwoPlusTwoCoachingIntegration, allRequiredAnswered);
            }
        }
    }
    public async confirmDeleteGoal(goal: Goal): Promise<void> {
        const alert = await this._alertController.create({
            message: this._translationService.getTranslation("goalProgressInfo.confirmDeleteGoal"),
            buttons: [
                {
                    text: this._translationService.getTranslation("generic.no"),
                    role: "cancel",
                    cssClass: "danger",
                },
                {
                    text: this._translationService.getTranslation("generic.yes"),
                    handler: () => {
                        this._deleteGoal(goal.id);
                    },
                },
            ],
        });

        await alert.present();
    }

    public async openNewGoalModal() {
        if (this.goals.length >= this.inputConfig.maximumGoalQuantity) {
            var message = this._translationService.getTranslation('goalsInput.maxGoalsQuantityReached');
            this.notify(message.replace('{{maxGoals}}', this.inputConfig.maximumGoalQuantity.toString()), 'danger');
            return;
        }

        const modal = await this._modalController.create({
            component: NewGoalsInput,
            cssClass: "new-goals-input",
            componentProps: {
                cycleParticipantId: this._participantId,
                processConfig: this.inputConfig,
                processType: this.ProcessType.GoalsReviewValidation,
            },
        });

        modal.onDidDismiss().then((dismissEvent) => {
            if (dismissEvent.data) {
                this._loadGoals(this._cycleId, this._employeeId);
            }
        });

        return await modal.present();
    }

    public goBack(): void {
        this._router.navigate([this._returnPath ? this._returnPath : "goals-review-validation/" + this._cycleId]);
    }

    public setQuestionIndex(question: QuestionConfig) {
        question.currentIndex = question.order;
        return question;
    }

    public setRangeValue(key: string, value: string): void {
        if (!this.coachingForm.get(key).disabled && this.canValidate) this.coachingForm.get(key).setValue(value);
    }

    public showRequiredBadge(formGroup: UntypedFormGroup, index: number): boolean {
        return formGroup["missingFields"] && !formGroup.get("field" + index + "comment").valid;
    }

    private _getInitialGoalApproval(goal: Goal): boolean | null {
        if (goal?.approved === true || goal?.approved === false) {
            return goal.approved;
        }

        switch (goal?.status) {
            case GoalStatusEnum.ReviewAccepted:
                return true;
            case GoalStatusEnum.ReviewRejected:
                return false;
            default:
                return null;
        }
    }

    private _getInitialQuestionApproval(coachingRecord: TwoPlusTwoCoaching, commentControl: string): boolean | null {
        if (!coachingRecord) {
            return null;
        }

        // Se está aguardando autorização, deve estar em estado neutro (sem decisão neste ciclo)
        if (coachingRecord.status === TwoPlusTwoCoachingStatus.AwaitingAuthorization || coachingRecord.status === TwoPlusTwoCoachingStatus.Draft) {
            return null;
        }

        return coachingRecord[commentControl] ? false : true;
    }

    private async _loadGoals(cycleId: number, employeeId: string): Promise<void> {
        const loading = await this._loadingController.create();
        await loading.present();

        this._goalsService.getUserGoalsForReview(cycleId, employeeId).subscribe(
            (response) => {
                this._participantId = response.data.cycleParticipantId;

                this.inputConfig = response.data.goalInputConfig;
                this.reviewConfig = response.data.goalReviewConfig;
                this.showSkipTwoPlusTwoCoachingEvaluation = this.showSkipTwoPlusTwoCoachingEvaluationFlag && this.reviewConfig.skipTwoPlusTwoCoachingEvaluation;
                this.isUserAttributesInsideConsentGoals = response.data.isUserAttributesInsideConsentGoals;
                this.coachingRecordStatus = response.data.userTwoPlusTwoCoachingRecord?.status;

                response.data.goals.forEach((goal: Goal) => {
                    goal.approved = this._getInitialGoalApproval(goal);
                });

                this.user = response.data.user;
                this.goals = response.data.goals;
                this.displayedGoals = response.data.goals;
                this.userPreview = this._setUserPreview(this.goals, this.user);

                this.canValidate =
                    this.goals.some(
                        (g) =>
                            response.data.processStatus !== ProcessStatusEnum.AllowedViewing &&
                            (g.status === GoalStatusEnum.WaitingForApproval ||
                                g.status === GoalStatusEnum.Rejected ||
                                g.status === GoalStatusEnum.DraftForReview ||
                                g.status === GoalStatusEnum.SentForReview ||
                                g.status === GoalStatusEnum.ReviewRejected ||
                                g.status === GoalStatusEnum.ReviewAccepted ||
                                (response.data.userTwoPlusTwoCoachingRecord &&
                                    response.data.userTwoPlusTwoCoachingRecord.status === TwoPlusTwoCoachingStatus.AwaitingAuthorization) ||
                                !response.data.goalReviewConfig.twoPlusTwoCoachingIntegration),
                    ) ||
                    this.hideGoalsOnGoalsReview ||
                    this.isUserAttributesInsideConsentGoals;

                this.coachingConfig = response.data.twoPlusTwoCoachingConfig;
                this._normalizeCoachingConfigQuestions();

                this.coachingForm = this._createCoachingFormGroup(
                    this.coachingConfig.questionsGroups ?? [],
                    response.data.userTwoPlusTwoCoachingRecord,
                );

                loading.dismiss();
            },
            (error) => {
                loading.dismiss();
                this.notify(this.getErrorNotification(error), "danger");
            },
        );
    }

    private _getVisibleQuestions(): QuestionConfig[] {
        const attributes = this.user?.attributes;
        if (this.coachingConfig.questionsGroups?.length > 0) {
            const visibleGroups = this._questionGroupAttributesPipe.transform(
                this.coachingConfig.questionsGroups,
                attributes
            );
            return visibleGroups.reduce<QuestionConfig[]>((acc, g) =>
                acc.concat(this._questionAttributePipe.transform(g.questions, attributes, true))
            , []);
        }
        return this._questionAttributePipe.transform(this.coachingConfig.questions, attributes, true);
    }

    private _normalizeCoachingConfigQuestions(): void {
        if (!this.coachingConfig) return;

        const hasGroups = (this.coachingConfig.questionsGroups?.length ?? 0) > 0;
        const hasQuestions = (this.coachingConfig.questions?.length ?? 0) > 0;

        if (!hasGroups && hasQuestions) {
            const fallbackGroup: QuestionGroup = {
                order: 1,
                globalizationConfig: this.coachingConfig.globalizationConfig,
                questions: this.coachingConfig.questions,
                questionGroups: [],
                onlyCalculateQuestions: false,
                hasVisibleQuestions: true,
            };

            this.coachingConfig.questionsGroups = [fallbackGroup];
        }

        const groups = this.coachingConfig.questionsGroups;
        if (!groups || groups.length === 0) {
            this.coachingConfig.questions = [];
            this.coachingConfig.questionsGroups = [];
            return;
        }

        const orderedGroups = [...groups].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

        const flat: QuestionConfig[] = [];
        let idx = 1;

        orderedGroups.forEach(g => {
            const orderedQuestions = [...(g.questions ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
            orderedQuestions.forEach(q => {
                q.currentIndex = idx;
                q.order = idx;

                flat.push(q);
                idx++;
            });
        });

        this.coachingConfig.questions = flat;
        this.coachingConfig.questionsGroups = orderedGroups;
    }

    private _createCoachingFormGroup(questionsGroups: Array<any>, coachingRecord: TwoPlusTwoCoaching): UntypedFormGroup {
        const formGroup = new UntypedFormGroup({});
        let currentIndex = 1;

        questionsGroups.forEach((group) => {
            group.questions.forEach((question: QuestionConfig) => {
                const controlName = "field" + currentIndex;

                formGroup.addControl(
                    controlName,
                    new UntypedFormControl({
                        value: coachingRecord ? coachingRecord[controlName] : "",
                        disabled: this.canValidate === false,
                    }),
                );

                const commentControl = "field" + currentIndex + "Comment";
                formGroup.addControl(
                    commentControl,
                    new UntypedFormControl({
                        value: coachingRecord ? coachingRecord[commentControl] : "",
                        disabled: this.canValidate === false,
                    }),
                );

                question.approved = this._getInitialQuestionApproval(coachingRecord, commentControl);
                question.currentIndex = currentIndex;
                question.order = currentIndex;

                currentIndex++;
            });
        });

        this.generalComments = coachingRecord ? coachingRecord.managerComment : "";

        return formGroup;
    }

    private _setUserPreview(goals: Array<Goal>, user: UserPreview): UserPreview {
        if (this.hideGoalsOnGoalsReview) {
            return {
                imageUrl: user ? user.imageUrl : null,
                username: user ? user.username : null,
                name: user ? user.name : null,
                group: user?.attributes?.find(a => a.type === AttributeType.BusinessGroup)?.name,
                rank: user?.attributes?.find(a => a.type === AttributeType.Rank)?.name,
            };
        }
        if (!goals || goals.length === 0) return null;
        return {
            imageUrl: goals[0].userImgUrl,
            username: goals[0].userName,
            group: user?.attributes?.find(a => a.type === AttributeType.BusinessGroup)?.name,
            rank: user?.attributes?.find(a => a.type === AttributeType.Rank)?.name,
        };
    }

    private _setSearchSubscription() {
        this._searchSubject.pipe(distinctUntilChanged(), debounceTime(1000)).subscribe((searchValue: string) => {
            this.displayedGoals = this.goals.filter((goal) => {
                return goal.title.toLowerCase().includes(searchValue.toLowerCase().trim());
            });
        });
    }

    private _deleteGoal(goalId: number): void {
        this._goalsService.deleteGoal(goalId).subscribe(
            () => {
                this._loadGoals(this._cycleId, this._employeeId);
                this.notify(this._translationService.getTranslation("goalsInput.goalDeleteSuccess"), "success");
            },
            (error) => this.notify(this.getErrorNotification(error), "danger"),
        );
    }

    private _getGoalsFeedbacks(goals: Array<Goal>): Array<GoalFeedback> {
        if (goals.some((g) => g.approved === null || g.approved === undefined)) {
            this.notifyError("goalsValidation.missingApprovals");
            return null;
        }

        if (goals.some((g) => g.approved === false && !g.managerComment)) {
            this.notifyError("goalsValidation.missingRejectedFeedback");
            return null;
        }

        return goals.map((g) => {
            return {
                goalId: g.id,
                approved: g.approved,
                comment: g.managerComment,
            };
        });
    }

    private _getCoachingReview(questions: Array<QuestionConfig>, coaching: TwoPlusTwoCoaching): TwoPlusTwoCoaching {
        if (questions.some((g) => g.approved === null || g.approved === undefined)) {
            this.notifyError("goalsValidation.missingApprovals");
            return null;
        }

        for (let index = 0; index < questions.length; index++) {
            const question = questions[index];

            const title = this.getText(question.globalizationConfig, "title");
            const detail = title ? `${title}` : `${question.order}`;

            if (question.approved === false && !coaching["field" + (index + 1) + "Comment"]) {
                const base = this._translationService.getTranslation("goalsValidation.missingRejectedFeedback");

                this.notify(`${base}: ${detail}`, "danger");
                return null;
            }

            if (!this.showSkipTwoPlusTwoCoachingEvaluationFlag && question.required && !question.hideQuestion && !coaching["field" + (question.order)]) {
                const base = this._translationService.getTranslation("generic.requiredValueNotProvided");

                this.notify(`${base}: ${detail}`, "danger");
                this.coachingForm["missingFields"] = true;
                return null;
            }
        }

        coaching.generalComments = this.generalComments;
        return coaching;
    }

    private _saveFeedbacks(coaching: TwoPlusTwoCoaching, feedbacks: Array<GoalFeedback>, isTwoPlusTwoCoachingIntegration: boolean, allRequiredAnswered: boolean): void {
        if (!this._validateGoals(feedbacks)) {
            return;
        }

        this._goalsService.saveGoalsReviewValidation(this._participantId, coaching, feedbacks, isTwoPlusTwoCoachingIntegration, allRequiredAnswered).subscribe(
            () => {
                this.notify(this._translationService.getTranslation("generic.infoSavedSuccessfully"), "success");
                this.goBack();
            },
            (error) => {
                
            if (error?.error?.errors?.includes('goalsInput.maxGoalsQuantityReached')) {
                var message = this._translationService.getTranslation('goalsInput.maxGoalsQuantityReached');
                this.notify(message.replace('{{maxGoals}}', this.inputConfig.maximumGoalQuantity.toString()), 'danger');
            }
            else
                this.notify(this.getErrorNotification(error), "danger")
            },
        );
    }

    private _validateGoals(goals: Array<GoalFeedback>) {
        if (this.inputConfig && goals.length > this.inputConfig.maximumGoalQuantity) {
            var message = this._translationService.getTranslation('goalsInput.maxGoalsQuantityReached');
            this.notify(message.replace('{{maxGoals}}', this.inputConfig.maximumGoalQuantity.toString()), 'danger');
            return false;
        }
        return true;
    }

    private _buildMissingFieldMessage(missing: QuestionConfig[]): string {

        const base = this._translationService.getTranslation("generic.fillMandatory");

        const details = missing
                            .map((q) => {
                                const title = this.getText(q.globalizationConfig, "title")
                                return title ? `${title}` : `${q.order}`;
                            }).join("  |  ");

        return details ? `${base}: ${details}` : base;

    }

    private _armMandatoryQuestionFlag(missing: QuestionConfig[], mode: 'answer' | 'comment' = 'answer') {
        this.coachingForm["missingFields"] = true;

        for (const q of missing){
            const idx = q.currentIndex ?? q.order;
            const controlName = mode === 'comment' ? `field${idx}Comment` : `field${idx}`;

            const ctrl = this.coachingForm.get(controlName);
            ctrl?.markAsTouched();
            ctrl?.markAsDirty();
            ctrl?.updateValueAndValidity({ onlySelf: true });
        }
    }



    @HostListener("window:beforeunload")
    ngOnDestroy() {
        this._searchSubject.unsubscribe();
        localStorage.removeItem("GoalsReview_ReturnPath");
    }
}
